import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import Badge from "@/v2-backoffice-client/components/badge"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  GROUPING_RANGE_DATES,
  summaryFraction,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import LicenseSeatBadge from "@/v2-backoffice-client/license/LicenseSeatBadge"
import LicenseStatusBadge from "@/v2-backoffice-client/license/LicenseStatusBadge"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import { LICENSE_SOURCE_OPERATOR } from "@/v2-backoffice-client/license/license.constants"
import LicensePlanSeatHolderRowToolbar from "@/v2-backoffice-client/license/plan/view/LicensePlanSeatHolderRowToolbar"
import { useLicensePlanSeatHoldersQuery } from "@/v2-backoffice-client/license/plan/view/license.plan.seatHolders.query"
import { buildUserColumns } from "@/v2-backoffice-client/user/user.columns"
import type { LicensePlanSeatHoldersQuery } from "@/v2-backoffice-client/license/plan/view/__types__/license.plan.seatHolders.query"

export type LicensePlanSeatHolderRow =
  LicensePlanSeatHoldersQuery["getLicensePlanSeatHolders"][number]

const SEAT_STATE_ACTIVE = "Active"
const SEAT_STATE_STALE = "Stale"

const COLUMN_SIZING = {
  Name: 175,
  UserStatusCode: 90,
  UserActivityLevelCode: 80,
  Email: 200,
  CustomerName: 170,
  LicenseStatusCode: 150,
  IsActiveUser: 90,
  Seats: 100,
  GrantedAt: 130,
  GrantedByUserName: 140,
  TenantNames: 150,
  ContactSyncStatus: 120,
  MigrationStatus: 130,
  LastSignInAt: 130,
  IdentityCreatedAt: 130,
  CreatedAt: 130,
}

// The CS columns of the standard user set start hidden here: this list is
// about seats on a license, not about the people's onboarding. They stay
// one click away in the columns panel.
const HIDDEN_USER_COLUMNS = ["ContactSyncStatus", "UsesV2", "MigrationStatus"]

function seatStateOf(holder: LicensePlanSeatHolderRow) {
  return holder.IsActiveUser ? SEAT_STATE_ACTIVE : SEAT_STATE_STALE
}

function createLicensePlanSeatHolderColumns(input: {
  statusNamesByCode: Map<number, string>
  licenseStatusOptions: { label: string; value: string }[]
  customerOptions: { label: string; value: string }[]
}) {
  const { statusNamesByCode, licenseStatusOptions, customerOptions } = input

  function statusName(holder: LicensePlanSeatHolderRow) {
    return (
      statusNamesByCode.get(holder.LicenseStatusCode) ??
      String(holder.LicenseStatusCode)
    )
  }

  // The standard user columns, the seat and license columns slotted in
  // after the person's identity, the rest of the user set after them.
  const {
    Name,
    UserStatusCode,
    UserActivityLevelCode,
    Email,
    ...moreUserColumns
  } = buildUserColumns<LicensePlanSeatHolderRow>({
    getUser: (row) => row.User,
    // Push onto the sheet stack — a CSM walks plan → person and back.
    buildTo: (row) => `>>?v=user:${row.UserId}`,
    buildUsageTo: (row) => `>>?v=user:${row.UserId}&user-view-tabs-tab=usage`,
    defaultHidden: HIDDEN_USER_COLUMNS,
  })

  return createColumns<LicensePlanSeatHolderRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      Name,
      UserStatusCode,
      UserActivityLevelCode,
      Email,
      CustomerName: {
        id: "CustomerName",
        label: "Customer",
        description:
          "The customer whose license the seat is on. Group on it to read the list one customer at a time, as the Seats dialog shows it.",
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any customer",
          options: customerOptions,
        },
        getValue: (row) => row.CustomerName ?? "",
        // Push onto the sheet stack — plan → customer and back. App-created
        // customers without a HubSpot id can't deep-link (the customer panel
        // is keyed by it).
        to: ({ row }) =>
          row.original.ExternalCustomerId
            ? `>>?v=customer:${row.original.ExternalCustomerId}`
            : undefined,
      },
      LicenseStatusCode: {
        id: "LicenseStatusCode",
        label: "License Status",
        description:
          "The state of the license the seat is on. The seat works while that license is live (Trial, Active, Not renewing); on an expired license it grants nothing.",
        enableGrouping: true,
        filter: {
          type: "multi-select",
          placeholder: "Any license status",
          options: licenseStatusOptions,
        },
        getValue: (row) => statusName(row),
        renderCell({ row }) {
          return (
            <LicenseStatusBadge
              license={row.original}
              statusName={statusNamesByCode.get(row.original.LicenseStatusCode)}
            />
          )
        },
        exportValue({ row }) {
          return statusName(row.original)
        },
        summaryValue({ row }) {
          return statusName(row.original)
        },
      },
      IsActiveUser: {
        id: "IsActiveUser",
        label: "Seat",
        description:
          "Active = the person is an active user of the license's customer and the seat lets them use the license. Stale = they have left, were blocked or moved to another customer: the seat grants nothing but still counts as a named person. Revoke it from the row menu.",
        enableGrouping: true,
        filter: {
          type: "select",
          placeholder: "Any seat",
          options: [
            { label: SEAT_STATE_ACTIVE, value: SEAT_STATE_ACTIVE },
            { label: SEAT_STATE_STALE, value: SEAT_STATE_STALE },
          ],
        },
        getValue: (row) => seatStateOf(row),
        renderCell({ row }) {
          if (row.original.IsActiveUser) {
            return <DotText variant="success-1">{SEAT_STATE_ACTIVE}</DotText>
          }
          return (
            <Badge
              size="sm"
              variant="warning"
              tooltip="No longer an active user of this customer (left, blocked or deleted). The seat grants nothing but still counts as a named person on the license — revoke it from the row menu."
            >
              {SEAT_STATE_STALE}
            </Badge>
          )
        },
        exportValue({ row }) {
          return seatStateOf(row.original)
        },
      },
      Seats: {
        id: "Seats",
        label: "Named / Paid",
        description:
          "People named on this customer's license against the seat count it carries. No count = the named people are the whole restriction; over the count is allowed and shown for billing.",
        filter: { type: "number-range", placeholder: "Paid seats" },
        getValue: (row) => row.Seats,
        renderCell({ row }) {
          const holder = row.original
          return (
            <LicenseSeatBadge
              occupied={holder.NamedSeatsOnLicense}
              cap={holder.Seats}
              tooltip="Named people against the licensed seats"
            />
          )
        },
        exportValue({ row }) {
          const holder = row.original
          return `${holder.NamedSeatsOnLicense} / ${holder.Seats ?? "—"}`
        },
        summaryValue({ row }) {
          const holder = row.original
          return summaryFraction(holder.NamedSeatsOnLicense, holder.Seats)
        },
      },
      GrantedAt: {
        id: "GrantedAt",
        label: "Seat Since",
        filter: { type: "date-filter" },
        groupingRanges: GROUPING_RANGE_DATES,
        renderCell({ row }) {
          return <RelativeDate date={row.original.GrantedAt} />
        },
        exportValue({ row }) {
          return formatDateString(row.original.GrantedAt)
        },
      },
      GrantedByUserName: {
        id: "GrantedByUserName",
        label: "Granted By",
        description:
          "Who named the person: a Placepoint operator in Backoffice or one of the customer's administrators in Fusion. System when the seat was granted mechanically.",
        enableGrouping: true,
        filter: { type: "text", placeholder: "Granted by..." },
        getValue: (row) => row.GrantedByUserName ?? "System",
        renderCell({ row }) {
          const value = row.original.GrantedByUserName
          if (!value) {
            return <span className="text-muted-foreground">System</span>
          }
          return value
        },
      },
      ...moreUserColumns,
      SeatsManagedByCustomer: {
        id: "SeatsManagedByCustomer",
        label: "Seat Policy",
        description:
          "Who assigns the seats on this license: Placepoint only, or also the customer's own administrators in Fusion.",
        defaultSize: 120,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "select",
          placeholder: "Any policy",
          options: [
            { label: "Customer admins", value: "Customer admins" },
            { label: "Placepoint only", value: "Placepoint only" },
          ],
        },
        getValue: (row) =>
          row.SeatsManagedByCustomer ? "Customer admins" : "Placepoint only",
      },
      LicenseSourceCode: {
        id: "LicenseSourceCode",
        label: "License Source",
        defaultSize: 110,
        defaultHidden: true,
        enableGrouping: true,
        filter: {
          type: "select",
          placeholder: "Any source",
          options: [
            { label: "Operator", value: "Operator" },
            { label: "Billing", value: "Billing" },
          ],
        },
        getValue: (row) =>
          row.LicenseSourceCode === LICENSE_SOURCE_OPERATOR
            ? "Operator"
            : "Billing",
      },
      LicenseId: {
        id: "LicenseId",
        label: "License Id",
        defaultSize: 70,
        defaultHidden: true,
      },
      CustomerId: {
        id: "CustomerId",
        label: "Customer Id",
        defaultSize: 70,
        defaultHidden: true,
      },
      UserId: {
        id: "UserId",
        label: "User Id",
        defaultSize: 70,
        defaultHidden: true,
      },
    },
  })
}

export type LicensePlanSeatHolderTableProps = Omit<
  TableProps<LicensePlanSeatHolderRow>,
  "data" | "columns"
> & {
  planId: number
  exportFileName?: string
  /**
   * Called alongside the table's own refetch on toolbar refresh — the sheet
   * hooks in the plan-row refetch so the tab badge refreshes with the list
   * it counts.
   */
  onRefresh?(): void
}

// The plan panel's Named Seats tab: every person named on a license of this
// plan, across all customers. The MCP products are priced per named person,
// so this is the one list of who the plan's customers are paying for. A
// user table like the others (the same Name, Status, Activity, Email,
// Team(s), usage and sign-in columns, from the row's User), with the seat
// and its license beside them: which customer, the license's status, whether
// the seat still grants anything, named against paid, since when and by whom.
// Group or filter on any of them (customer, license status, seat state). Owns
// its list query (plus the status-name lookup) — the data loads when the tab
// is first opened, not when the panel is. Seats are named per customer (the
// license row's Seats dialog) and revoked there or from this table's row menu.
function LicensePlanSeatHolderTable(props: LicensePlanSeatHolderTableProps) {
  const { className, planId, exportFileName, onRefresh, ...tableProps } = props

  const { data, loading, error, refetch } = useLicensePlanSeatHoldersQuery({
    variables: { planId },
  })
  const holders = data?.getLicensePlanSeatHolders ?? []

  const { data: statusesData } = useLicenseStatusesQuery()
  const licenseStatuses = statusesData?.getLicenseStatuses
  const statusNamesByCode = useMemo(
    () =>
      new Map(
        (licenseStatuses ?? []).map((status) => [status.Code, status.Name]),
      ),
    [licenseStatuses],
  )
  // Client-side filtering compares against getValue — the status name.
  const licenseStatusOptions = useMemo(
    () =>
      (licenseStatuses ?? []).map((status) => ({
        label: status.Name,
        value: status.Name,
      })),
    [licenseStatuses],
  )

  // The customers on the list, for the Customer filter: a plan has a handful
  // of them, and the ones with no seat would only be noise.
  const customerOptions = useMemo(() => {
    const names = new Set<string>()
    for (const holder of holders) {
      if (holder.CustomerName) names.add(holder.CustomerName)
    }
    return [...names]
      .toSorted((a, b) => a.localeCompare(b))
      .map((name) => ({ label: name, value: name }))
  }, [holders])

  const columns = useMemo(
    () =>
      createLicensePlanSeatHolderColumns({
        statusNamesByCode,
        licenseStatusOptions,
        customerOptions,
      }),
    [statusNamesByCode, licenseStatusOptions, customerOptions],
  )

  return (
    <Table
      className={cn("license-plan-seat-holder-table", className)}
      tableId="license-plan-seat-holder-table"
      data={holders}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      enableGrouping
      getRowId={(row) => `${row.LicenseId}:${row.UserId}`}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        {/* Global search: name, email, customer, team — standardized across
            every user-shaped table. */}
        <Table.Search placeholder="Search by name or email..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => {
            void refetch()
            onRefresh?.()
          }}
          exportFileName={exportFileName ?? "license-named-seats"}
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.RowToolbar<LicensePlanSeatHolderRow> column="Name">
        {(holder) => <LicensePlanSeatHolderRowToolbar holder={holder} />}
      </Table.RowToolbar>

      <Table.Content
        countLabel="seats"
        isLoading={loading}
        error={error}
        // The guidance is for a plan nobody is named on; a filter or search
        // that hides every row gets the table's own "no match" state, or an
        // operator who filtered on Expired would read that nobody is named.
        renderEmpty={
          holders.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              Nobody is named on this license yet. Seats are named per customer,
              in the license row&apos;s Seats dialog on the customer panel.
            </p>
          ) : (
            <p className="p-4 text-center text-sm text-muted-foreground">
              No seat matches the search or filters. Clear them above to see
              every seat on this license again.
            </p>
          )
        }
      />
    </Table>
  )
}

export default LicensePlanSeatHolderTable
